import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { Empty } from 'antd';
import ChartCard from './ChartCard';
import { apiClient, API_ENDPOINTS } from '../../utils/api';
import styled from 'styled-components';

const COLORS = {
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  pending: '#FBBF24',
  processing: '#22D3EE',
  completed: '#10B981',
  failed: '#F87171',
};

const ChartContainer = styled.div`
  width: 100%;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
`;


interface SweepStatusDistribution {
  PENDING: number;
  PROCESSING: number;
  COMPLETED: number;
  FAILED: number;
}

const SweepStatusDistributionChart: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SweepStatusDistribution | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_SWEEP_STATUS_DISTRIBUTION);
      if (response.data?.status === 'success' && response.data?.data) {
        setData(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching sweep status distribution:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartData = data ? [
    { name: 'Pending', value: data.PENDING, color: COLORS.pending },
    { name: 'Processing', value: data.PROCESSING, color: COLORS.processing },
    { name: 'Completed', value: data.COMPLETED, color: COLORS.completed },
    { name: 'Failed', value: data.FAILED, color: COLORS.failed },
  ].filter(item => item.value > 0) : [];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = chartData.reduce((sum, item) => sum + item.value, 0);
      const percentage = total > 0 ? (data.value / total) * 100 : 0;
      return (
        <div style={{
          background: '#161B22',
          border: '1px solid #1f2a37',
          borderRadius: '8px',
          padding: '12px',
          color: COLORS.textPrimary,
        }}>
          <p style={{ margin: 0, marginBottom: '4px', fontWeight: 600 }}>
            {data.name}
          </p>
          <p style={{ margin: 0, color: COLORS.textSecondary }}>
            Count: {data.value}
          </p>
          <p style={{ margin: 0, color: COLORS.textSecondary }}>
            Percentage: {percentage.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null;

    return (
      <text
        x={x}
        y={y}
        fill={COLORS.textPrimary}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <ChartCard title="Sweep Status Distribution" loading={loading}>
      {!loading && (!data || chartData.length === 0) ? (
        <Empty description="No sweep status data available" />
      ) : (
        <ChartContainer>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={CustomLabel}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ color: COLORS.textPrimary }}
                iconType="circle"
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>
      )}
    </ChartCard>
  );
};

export default SweepStatusDistributionChart;

